import { BarChart3, TrendingUp, Target, RefreshCw } from 'lucide-react'
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver'

const features = [
  {
    icon: BarChart3,
    title: 'KPIs em Tempo Real',
    description: 'Profit Factor, Win Rate, ROI e mais métricas calculadas automaticamente',
  },
  {
    icon: TrendingUp,
    title: 'Curva de Equity',
    description: 'Visualize a evolução do seu saldo ao longo do tempo com gráficos interativos',
  },
  {
    icon: Target,
    title: 'Análise por Ativo',
    description: 'Win rate detalhado de cada par que você opera para otimizar estratégias',
  },
  {
    icon: RefreshCw,
    title: 'Sincronização Automática',
    description: 'Dados atualizados direto da sua conta FuturaBroker a cada 5 minutos',
  },
]

export function Features() {
  const { ref, isVisible } = useIntersectionObserver({ threshold: 0.1 })

  return (
    <section id="features" ref={ref} className="relative py-32 px-4 bg-black overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.01)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.01)_1px,transparent_1px)] bg-[size:64px_64px]" />

        {/* Radial Gradient */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.02)_0%,transparent_50%)]" />
      </div>

      <div className="relative container mx-auto">
        {/* Section Header */}
        <div
          className={`text-center mb-20 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
        >
          <span className="inline-block text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-500 mb-4">
            Recursos
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 tracking-tight">
            <span className="bg-gradient-to-b from-white via-gray-200 to-gray-500 bg-clip-text text-transparent">
              Tudo que você precisa
            </span>
          </h2>
          <p className="text-base text-gray-500 max-w-lg mx-auto leading-relaxed">
            Ferramentas profissionais para gerenciar sua banca de forma inteligente
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {features.map((feature, index) => (
            <FeatureCard
              key={feature.title}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
              delay={index * 0.15}
              isVisible={isVisible}
            />
          ))}
        </div>
      </div>
    </section>
  )
}

interface FeatureCardProps {
  icon: React.ElementType
  title: string
  description: string
  delay: number
  isVisible: boolean
}

function FeatureCard({ icon: Icon, title, description, delay, isVisible }: FeatureCardProps) {
  return (
    <div
      className={`group relative transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
      }`}
      style={{ transitionDelay: `${delay}s` }}
    >
      {/* Card Glow on Hover */}
      <div className="absolute -inset-px rounded-2xl bg-gradient-to-b from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      {/* Card Content */}
      <div className="relative h-full bg-white/[0.02] backdrop-blur-sm border border-white/[0.06] rounded-2xl p-8 transition-all duration-500 group-hover:border-white/10 group-hover:bg-white/[0.04]">
        {/* Icon */}
        <div className="relative mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white/[0.04] border border-white/[0.08] transition-all duration-500 group-hover:border-white/20 group-hover:bg-white/[0.08]">
            <Icon className="w-5 h-5 text-white/80 transition-colors group-hover:text-white" />
          </div>
        </div>

        {/* Content */}
        <h3 className="text-lg font-semibold mb-3 text-white tracking-tight">{title}</h3>
        <p className="text-sm text-gray-500 leading-relaxed">{description}</p>

        {/* Bottom Line Accent */}
        <div className="absolute bottom-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      </div>
    </div>
  )
}
